import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  User, 
  Mail, 
  MapPin, 
  Phone, 
  Shield, 
  Edit3, 
  Check, 
  X,
  Plus
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const ProfileScreen = () => {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Address State
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    isDefault: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await updateProfile({ name, email, password });
      setMessage('Profile updated successfully');
      setIsEditing(false);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/users/address`, newAddress, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      // Update user state locally or re-fetch
      // For now, let's assume updateProfile or similar updates the user
      setShowAddressModal(false);
      setNewAddress({ fullName: '', phone: '', address: '', city: '', state: '', postalCode: '', isDefault: false });
      window.location.reload(); // Refresh to show new address (could be optimized)
    } catch (err) {
      setError('Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 py-20 px-4">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-4">
            <div className="inline-flex items-center space-x-2 px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-[10px] font-black uppercase tracking-widest">
              <Shield className="h-3 w-3" />
              <span>Verified Account</span>
            </div>
            <h1 className="text-5xl font-black italic text-secondary-900 uppercase tracking-tighter">
              My Profile
            </h1>
          </div>
          <button 
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2 px-6 py-3 bg-white text-secondary-900 rounded-2xl font-bold shadow-sm border border-secondary-100 hover:shadow-md transition-all"
          >
            {isEditing ? <X className="h-5 w-5" /> : <Edit3 className="h-5 w-5" />}
            <span>{isEditing ? 'Cancel Edit' : 'Edit Profile'}</span>
          </button>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-4 rounded-2xl font-bold border border-red-100">{error}</div>}
        {message && <div className="bg-emerald-50 text-emerald-600 p-4 rounded-2xl font-bold border border-emerald-100">{message}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Account Settings */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-[40px] p-8 shadow-sm border border-secondary-100 space-y-8 h-full">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                  {user?.name?.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-black text-secondary-900">{user?.name}</h3>
                  <p className="text-secondary-500 font-medium">{user?.isAdmin ? 'Administrator' : 'Premium Member'}</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-secondary-400 uppercase tracking-widest ml-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-3.5 h-5 w-5 text-secondary-400" />
                    <input 
                      type="text" 
                      disabled={!isEditing}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-secondary-400 uppercase tracking-widest ml-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-3.5 h-5 w-5 text-secondary-400" />
                    <input 
                      type="email" 
                      disabled={!isEditing}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 disabled:opacity-50"
                    />
                  </div>
                </div>

                {isEditing && (
                  <>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-secondary-400 uppercase tracking-widest ml-1">New Password</label>
                      <input 
                        type="password" 
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Leave blank to keep current"
                        className="w-full bg-secondary-50 border-none rounded-2xl py-3.5 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-secondary-400 uppercase tracking-widest ml-1">Confirm Password</label>
                      <input 
                        type="password" 
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-secondary-50 border-none rounded-2xl py-3.5 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                      />
                    </div>
                    <button 
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-black italic uppercase shadow-xl shadow-primary-200 transition-all flex items-center justify-center space-x-2"
                    >
                      {loading ? 'Updating...' : <><Check className="h-5 w-5" /><span>Save Changes</span></>}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>

          {/* Addresses Section */}
          <div className="lg:col-span-2 space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tight">Saved Addresses</h2>
              <button 
                onClick={() => setShowAddressModal(true)}
                className="flex items-center space-x-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl font-bold shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all"
              >
                <Plus className="h-5 w-5" />
                <span>Add Address</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {user?.addresses?.length === 0 ? (
                <div className="col-span-full py-12 text-center bg-white rounded-[40px] border border-dashed border-secondary-200">
                  <MapPin className="h-12 w-12 text-secondary-200 mx-auto mb-4" />
                  <p className="text-secondary-400 font-bold uppercase text-sm tracking-widest">No addresses saved yet.</p>
                </div>
              ) : (
                user?.addresses?.map((addr) => (
                  <div key={addr._id} className="bg-white rounded-[32px] p-6 shadow-sm border border-secondary-100 hover:shadow-md transition-all group relative">
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="p-3 bg-secondary-50 rounded-2xl group-hover:bg-primary-50 transition-colors">
                          <MapPin className="h-6 w-6 text-secondary-400 group-hover:text-primary-600 transition-colors" />
                        </div>
                        {addr.isDefault && (
                          <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-[10px] font-black uppercase tracking-widest">Default</span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-black text-secondary-900">{addr.fullName}</h4>
                        <p className="text-sm text-secondary-500 font-medium">{addr.address}</p>
                        <p className="text-sm text-secondary-500 font-medium">{addr.city}, {addr.state} - {addr.postalCode}</p>
                      </div>
                      <div className="flex items-center space-x-2 text-sm font-bold text-secondary-400">
                        <Phone className="h-4 w-4" />
                        <span>{addr.phone}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-md my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-black italic text-secondary-900 uppercase">New Address</h2>
                <button onClick={() => setShowAddressModal(false)} className="p-2 hover:bg-secondary-100 rounded-full transition-all">
                  <X className="h-6 w-6 text-secondary-400" />
                </button>
              </div>

              <form onSubmit={handleAddAddress} className="space-y-4">
                <input required placeholder="Full Name" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                  value={newAddress.fullName} onChange={(e) => setNewAddress({...newAddress, fullName: e.target.value})} />
                <input required placeholder="Phone Number" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                  value={newAddress.phone} onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})} />
                <textarea required placeholder="Full Address" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                  value={newAddress.address} onChange={(e) => setNewAddress({...newAddress, address: e.target.value})} />
                <div className="grid grid-cols-2 gap-4">
                  <input required placeholder="City" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                    value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} />
                  <input required placeholder="State" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                    value={newAddress.state} onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} />
                </div>
                <input required placeholder="Postal Code" className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
                  value={newAddress.postalCode} onChange={(e) => setNewAddress({...newAddress, postalCode: e.target.value})} />
                
                <div className="flex items-center p-4 bg-secondary-50 rounded-2xl">
                  <input type="checkbox" id="isDefault" checked={newAddress.isDefault} onChange={(e) => setNewAddress({...newAddress, isDefault: e.target.checked})}
                    className="h-5 w-5 rounded text-primary-600 focus:ring-primary-500 border-none" />
                  <label htmlFor="isDefault" className="ml-3 font-bold text-secondary-700">Set as default address</label>
                </div>

                <button type="submit" className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-black italic uppercase shadow-xl shadow-primary-200 transition-all">
                  Save Address
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
